import React, { useCallback, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import BigNumber from 'bignumber.js'
import { VectorRightIcon } from 'uikit'
import styled from 'styled-components'
import { useFarms, usePriceDmdUsdc } from 'state/hooks'
import { Farm } from 'state/types'
import { useTranslation } from 'contexts/Localization'
import { getFarmApr } from 'utils/apr'
import isArchivedPid from 'utils/farmHelpers'
import FarmsHeader from './FarmsHeader'
import FarmItem, { FarmWithStakedValue } from './FarmItem'
import Pagination from '../Pagination'
import usePagination from '../../hooks/usePagination'

const FarmsCon = styled.div`
  margin-bottom: 32px;
`

const Head = styled.div`
  font-family: Poppins;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  margin-bottom: 12px;
`
const Title = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 30px;
  line-height: 45px;
`
const Start = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
`

const FarmsContainer = styled.div`
  background: #002130;
  border-radius: 12px;
`

const Row = styled.div`
  display: row;
  margin-top: 12px;
  margin-bottom: 4px;
`

const Farms: React.FC = () => {
  const { t } = useTranslation()
  const { data: farmsLP } = useFarms()
  const dmdPrice = usePriceDmdUsdc()

  const activeFarms = farmsLP.filter((farm) => farm.pid !== 0 && farm.multiplier !== '0X' && !isArchivedPid(farm.pid))

  const farmsList = useCallback(
    (farmsToDisplay: Farm[]): FarmWithStakedValue[] => {
      let farmsToDisplayWithAPR: FarmWithStakedValue[] = farmsToDisplay.map((farm) => {
        if (!farm.lpTotalInQuoteToken || !farm.quoteToken.usdcPrice) {
          return farm
        }
        const totalLiquidity = new BigNumber(farm.lpTotalInQuoteToken).times(farm.quoteToken.usdcPrice)
        const { cakeRewardsApr, lpRewardsApr } = getFarmApr(
          new BigNumber(farm.poolWeight),
          dmdPrice,
          totalLiquidity,
          farm.address[process.env.REACT_APP_CHAIN_ID],
        )
        return { ...farm, apr: cakeRewardsApr, lpRewardsApr, liquidity: totalLiquidity }
      })
      farmsToDisplayWithAPR = farmsToDisplayWithAPR.filter((farm) => !farm.isTokenOnly)

      return farmsToDisplayWithAPR
    },
    [dmdPrice],
  )

  const { pages, currentPage, nextPage, prevPage } = usePagination(5, farmsList(activeFarms).length)

  const farmsListShow = useMemo(() => {
    return farmsList(activeFarms).slice((currentPage - 1) * 5, currentPage * 5)
  }, [farmsList, activeFarms, currentPage])

  return (
    <FarmsCon>
      <Head>
        <Title>{t('Farms')}</Title>
        <NavLink to="/farms">
          <Start>
            {t('Start Farming')}
            <VectorRightIcon ml={16} fill="#FFFFFF" />
          </Start>
        </NavLink>
      </Head>
      <FarmsContainer>
        <FarmsHeader />
        <Row>
          {farmsListShow.map((farm, index) => (
            <FarmItem key={farm.pid} farm={farm} no={index + 1 + (currentPage - 1) * 5} />
          ))}
        </Row>
        <Pagination current={currentPage} pages={pages} nextPage={nextPage} prevPage={prevPage} />
      </FarmsContainer>
    </FarmsCon>
  )
}

export default Farms
